// Renderer for building objects

import * as THREE from 'three'
import type { GardenScene } from './GardenScene'
import { modelLoader } from './ModelLoader'
import { elevationService } from './ElevationService'
import { debug } from '@/utils/debug'
import { DPM_SCALE } from '@/constants/coordinates'
import type { Building, Product, ProductFile } from '@/types'

export class BuildingRenderer {
  private scene: GardenScene
  private meshes: Map<string, THREE.Object3D> = new Map()
  private products: Map<string, Product> = new Map()

  constructor(scene: GardenScene) {
    this.scene = scene
  }

  /**
   * Render all buildings
   */
  async renderBuildings(
    buildings: Building[],
    modelUrls: Map<string, ProductFile[]>,
    products: Map<string, Product> = new Map()
  ): Promise<void> {
    this.products = products

    // Validate scene is available
    if (!this.scene || !this.scene.buildingsGroup) {
      throw new Error('BuildingRenderer: Scene or buildingsGroup is not available')
    }

    // Clear existing building meshes
    this.clear()

    // Group buildings by product
    const buildingsByProduct = new Map<string, Building[]>()
    for (const building of buildings) {
      if (building.markedForDestruction) continue

      const productId = building.productId || 'default'
      if (!buildingsByProduct.has(productId)) {
        buildingsByProduct.set(productId, [])
      }
      buildingsByProduct.get(productId)!.push(building)
    }

    // Render each group
    for (const [productId, productBuildings] of buildingsByProduct) {
      const files = modelUrls.get(productId)
      const modelUrl = files?.find(f => f.kind === 'glb')?.fileUrl

      if (modelUrl) {
        await this.renderBuildingGroup(productBuildings, modelUrl)
      } else {
        // Fallback: render as simple box using product dimensions
        this.renderBuildingGroupFallback(productBuildings)
      }
    }
  }

  /**
   * Render a group of buildings using a GLTF model
   */
  private async renderBuildingGroup(buildings: Building[], modelUrl: string): Promise<void> {
    try {
      await modelLoader.load(modelUrl)

      for (const building of buildings) {
        // Verify scene is still valid after async operations
        if (!this.scene || !this.scene.buildingsGroup) {
          debug.warn('Building', 'Scene became unavailable during building rendering')
          return
        }

        const mesh = await modelLoader.createInstance(modelUrl)
        this.setupBuildingMesh(mesh, building)
        this.scene.buildingsGroup.add(mesh)
        this.meshes.set(building.id, mesh)
      }
    } catch (error) {
      debug.warn('Building', 'Failed to load building model, using fallback', error)
      this.renderBuildingGroupFallback(buildings)
    }
  }

  /**
   * Render buildings without models (simple box shapes)
   * Uses light gray semi-transparent box with 1m height
   * Dimensions from product width/height
   */
  private renderBuildingGroupFallback(buildings: Building[]): void {
    // Verify scene is available
    if (!this.scene || !this.scene.buildingsGroup) {
      debug.warn('Building', 'Scene unavailable for fallback building rendering')
      return
    }

    // Shared material for all fallback buildings
    const material = new THREE.MeshStandardMaterial({
      color: 0xcccccc, // Light gray
      transparent: true,
      opacity: 0.6,
      roughness: 0.8
    })

    const fallbackHeight = 1.0 // 1 meter height

    for (const building of buildings) {
      // Get product dimensions if available, fallback to building dimensions
      const product = building.productId ? this.products.get(building.productId) : undefined
      const widthDots = (product?.width ?? building.width) || 100
      const depthDots = (product?.height ?? building.height) || 100
      const width = widthDots * DPM_SCALE
      const depth = depthDots * DPM_SCALE

      debug.log('Building', `Fallback box for ${product?.code || building.id}: ${width.toFixed(2)}m x ${depth.toFixed(2)}m`)

      const geometry = new THREE.BoxGeometry(width, fallbackHeight, depth)
      const mesh = new THREE.Mesh(geometry, material)
      this.setupBuildingMesh(mesh, building, fallbackHeight)
      this.scene.buildingsGroup.add(mesh)
      this.meshes.set(building.id, mesh)
    }
  }

  /**
   * Setup mesh position, scale, and rotation based on building data
   */
  private setupBuildingMesh(
    mesh: THREE.Object3D,
    building: Building,
    fallbackHeight?: number,
  ): void {
    // x,y = center (converted from API top-left on import)
    const centerX = building.x
    const centerY = building.y

    // Get ground elevation beneath building (building.elevation is a modifier for plants, not used here)
    const groundElevation = elevationService.getGroundElevation(centerX, centerY)

    // For fallback boxes, position at half height; for models, at ground level
    const yPos = fallbackHeight
      ? groundElevation * DPM_SCALE + fallbackHeight / 2
      : groundElevation * DPM_SCALE

    mesh.position.set(
      centerX * DPM_SCALE,
      yPos,
      centerY * DPM_SCALE
    )

    // GLB models are authored at real-world scale (meters), no scaling needed
    // (fallback boxes handle their own sizing via geometry)

    // Apply rotation
    mesh.rotation.y = -building.rotation * Math.PI / 180

    // Enable shadows
    mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true
        child.receiveShadow = true
      }
    })

    // Store building ID for potential interaction
    mesh.userData.buildingId = building.id
    mesh.userData.buildingData = building
    if (fallbackHeight) mesh.userData.fallbackHeight = fallbackHeight
  }

  /**
   * Update a single building's position/rotation in place (no mesh recreation).
   * Analogous to PlantRenderer.updatePlant().
   */
  updateBuilding(building: Building): void {
    const mesh = this.meshes.get(building.id)
    if (!mesh) return

    const centerX = building.x
    const centerY = building.y
    const groundElevation = elevationService.getGroundElevation(centerX, centerY)
    const fallbackHeight = mesh.userData.fallbackHeight as number | undefined
    const yPos = fallbackHeight
      ? groundElevation * DPM_SCALE + fallbackHeight / 2
      : groundElevation * DPM_SCALE

    mesh.position.set(
      centerX * DPM_SCALE,
      yPos,
      centerY * DPM_SCALE
    )
    mesh.rotation.y = -building.rotation * Math.PI / 180
    mesh.userData.buildingData = building
  }

  /**
   * Re-query elevation service and update Y positions for all buildings.
   * Called after ground shapes change (move/resize) to keep buildings at correct height.
   */
  refreshElevations(): void {
    for (const [, mesh] of this.meshes) {
      const building = mesh.userData.buildingData as Building | undefined
      if (!building) continue
      const centerX = building.x
      const centerY = building.y
      const groundElevation = elevationService.getGroundElevation(centerX, centerY)
      const fallbackHeight = mesh.userData.fallbackHeight as number | undefined
      const yPos = fallbackHeight
        ? groundElevation * DPM_SCALE + fallbackHeight / 2
        : groundElevation * DPM_SCALE
      mesh.position.y = yPos
    }
  }

  /**
   * Add a single building without clearing existing ones
   */
  async addBuilding(
    building: Building,
    modelUrls: Map<string, ProductFile[]>,
    products: Map<string, Product> = new Map()
  ): Promise<void> {
    this.products = products
    if (!this.scene || !this.scene.buildingsGroup) return

    const productId = building.productId || 'default'
    const files = modelUrls.get(productId)
    const modelUrl = files?.find(f => f.kind === 'glb')?.fileUrl

    if (modelUrl) {
      try {
        await modelLoader.load(modelUrl)
        const mesh = await modelLoader.createInstance(modelUrl)
        this.setupBuildingMesh(mesh, building)
        this.scene.buildingsGroup.add(mesh)
        this.meshes.set(building.id, mesh)
      } catch {
        this.renderBuildingGroupFallback([building])
      }
    } else {
      this.renderBuildingGroupFallback([building])
    }
  }

  /**
   * Get a building mesh by ID
   */
  getMesh(buildingId: string): THREE.Object3D | undefined {
    return this.meshes.get(buildingId)
  }

  /**
   * Remove a building mesh
   */
  removeBuilding(buildingId: string): void {
    const mesh = this.meshes.get(buildingId)
    if (mesh) {
      this.scene.buildingsGroup.remove(mesh)
      this.meshes.delete(buildingId)
    }
  }

  /**
   * Clear all building meshes
   */
  clear(): void {
    if (this.scene && this.scene.buildingsGroup) {
      this.scene.clearGroup(this.scene.buildingsGroup)
    }
    this.meshes.clear()
  }

  /**
   * Get building count
   */
  get count(): number {
    return this.meshes.size
  }
}
